import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Subscription } from 'rxjs';
import { useDispatch, useSelector } from 'react-redux';
import { PhoneIphone as MobileIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { Device, DeviceID } from '@playq/octopus2-devices';

import { onlineDevicesManagement, services2, unwrapEither } from '/api';
import { useLocalStorage, useModal } from '/hooks';
import { deviceToolkit, globalDispatch } from '/store';
import { SelectDialog } from '/shared/SelectDialog';
import { EventType, onEvent } from '/common/buzzer';
import { snackbarService } from '/common/snackbarService';

import { BadgeStyled, ButtonStyled } from './styles';
import { checkNameInDevice } from './helpers';
import { ButtonGroup } from './components';
import { DeviceTable } from './DeviceTable';
import { LoadingTextProgress } from './ProgressText';
import { StorageDevice, SubscribeDevice } from './types';

export const OnlineDevices: FC = memo(() => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const subscribtions = useSelector(deviceToolkit.selectors.devices);
  const subscriptionDevice = useSelector(deviceToolkit.selectors.subscribtionDevice);
  const [loadingConnection, setLoadingConnection] = useState(false);
  const { open, close, isOpen } = useModal();
  const { subscribeDevice, unsubscribeDevice } = globalDispatch(dispatch);
  const [storedDevice, setStoredDevice, removeItem] = useLocalStorage<StorageDevice | null>(
    'DeviceID',
    null,
    handleDeviceSubscription
  );

  function handleDeviceSubscription(updatedData: StorageDevice | null) {
    if (updatedData) {
      subscribeDevice(checkNameInDevice(updatedData.device));
    } else {
      unsubscribeDevice(subscribtions[0]?.id);
    }
  }

  const location = useLocation();
  const hasNotSubscribtions = subscribtions.length === 0;
  const refreshDevice = useCallback(() => {
    return queryClient.invalidateQueries({ queryKey: onlineDevicesManagement });
  }, [queryClient]);

  const changeSubscription = (currentId: string, pickedDevice: Device) => {
    if (currentId !== pickedDevice.id.serialize()) {
      unwrapEither(services2.onlineDevicesEphemeral.sendUnsubscription(new DeviceID(currentId)))
        .then(() => {
          subscribe({ deviceID: pickedDevice.id.serialize(), device: pickedDevice });
        })
        .catch((err) => {
          console.error('Unsubscription error during change:', err);
          setLoadingConnection(false);
        });
    }
  };
  const unsubscribe = useCallback(
    (closeDialog = true) => {
      if (hasNotSubscribtions) return;

      unwrapEither(services2.onlineDevicesEphemeral.sendUnsubscription(new DeviceID(subscribtions[0].id)))
        .then(() => {
          removeItem();
          if (closeDialog) {
            close();
          }
          setLoadingConnection(false);
        })
        .catch((err) => {
          console.error('Unsubscription error:', err);
          setLoadingConnection(false);
        });
    },
    [close, hasNotSubscribtions, removeItem, subscribtions]
  );

  const subscribe = useCallback(
    ({ deviceID, device, reloadPage = false }: SubscribeDevice) => {
      unwrapEither(services2.onlineDevicesEphemeral.sendSubscription(new DeviceID(deviceID)))
        .then((res) => {
          const status = { ...device.serialize(), connection: res.connection?.serialize() };
          setStoredDevice({ deviceID, device: status });
          if (reloadPage) {
            setLoadingConnection(false);
          }
        })
        .catch((err) => {
          removeItem();
          setLoadingConnection(false);
          console.error(err, 'Subscription error');
          snackbarService.error('Subscription error, check devtools');
        });
    },
    [removeItem, setStoredDevice]
  );

  useEffect(() => {
    const subscription = new Subscription();
    subscription.add(
      onEvent(EventType.DEVICE_DISCONNECTED).subscribe(() => {
        refreshDevice();
      })
    );

    subscription.add(
      onEvent(EventType.DEVICE_CONNECTED).subscribe(() => {
        refreshDevice();
      })
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [location.pathname, refreshDevice]);
  useEffect(() => {
    let reconnectTimer: number | null = null;

    if (storedDevice !== null && !subscriptionDevice?.device) {
      setLoadingConnection(true);

      reconnectTimer = window.setTimeout(() => {
        subscribe({
          deviceID: storedDevice.deviceID,
          device: checkNameInDevice(storedDevice.device),
          reloadPage: true,
        });
      }, 2000);
    }

    return () => {
      if (typeof reconnectTimer === 'number') {
        clearTimeout(reconnectTimer);
      }
    };
  }, [subscriptionDevice?.device, subscribe, storedDevice]);

  return (
    <>
      <Tooltip title='View devices' placement='bottom'>
        <ButtonStyled onClick={open} color='inherit'>
          <BadgeStyled
            $offline={subscriptionDevice?.device.connection === undefined}
            invisible={subscriptionDevice?.device === undefined}
            variant='dot'
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'bottom',
            }}
          >
            <MobileIcon />
          </BadgeStyled>
          {loadingConnection ? <LoadingTextProgress /> : subscriptionDevice?.name}
        </ButtonStyled>
      </Tooltip>
      {isOpen && (
        <SelectDialog
          title='Devices'
          open={isOpen}
          onClose={close}
          actions={
            <ButtonGroup
              tooltipText={`${hasNotSubscribtions ? 'You are not subscribed' : ''}`}
              refetch={refreshDevice}
              unsubscribe={unsubscribe}
              disabled={hasNotSubscribtions}
            />
          }
          content={
            <DeviceTable
              setStoredDevice={setStoredDevice}
              subscribeDevice={subscribe}
              unsubscribe={unsubscribe}
              changeSubscription={changeSubscription}
            />
          }
        />
      )}
    </>
  );
});
